import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EMOJI_MAP = {
  1: { emoji: '😞', label: 'Very Dissatisfied', color: '#dc2626' },
  2: { emoji: '🙁', label: 'Dissatisfied',       color: '#f97316' },
  3: { emoji: '😐', label: 'Neutral',             color: '#eab308' },
  4: { emoji: '🙂', label: 'Satisfied',           color: '#22c55e' },
  5: { emoji: '😍', label: 'Very Satisfied',      color: '#10b981' },
};

function StarFill({ rating }) {
  const full = Math.floor(rating);
  const partial = rating - full;
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          style={{
            width: 20, height: 20, borderRadius: 3,
            background: i <= full ? '#f59e0b' : i === full + 1 && partial > 0 ? `linear-gradient(90deg, #f59e0b ${partial * 100}%, #e5e7eb ${partial * 100}%)` : '#e5e7eb',
          }}
        />
      ))}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function PortalFeedbackPage() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [filterPage, setFilterPage] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [page, setPage]           = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);

  // Page breakdown sort
  const [pbSort, setPbSort] = useState({ col: 'count', dir: 'desc' });

  const load = useCallback(async (pg = 1, fp = filterPage, fr = filterRating) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: 50 };
      if (fp) params.page_path = fp;
      if (fr) params.rating = fr;
      const { data: d } = await axios.get(`${API}/api/admin/portal/feedback`, { params, withCredentials: true });
      setData(d);
    } catch {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [filterPage, filterRating]);

  useEffect(() => { load(1); }, []);  // initial load only

  const applyFilters = () => { setPage(1); load(1, filterPage, filterRating); };
  const clearFilters = () => {
    setFilterPage('');
    setFilterRating('');
    setPage(1);
    load(1, '', '');
  };

  const summary     = data?.summary || {};
  const breakdown   = data?.breakdown || [];
  const pageBreakdown = [...(data?.page_breakdown || [])].sort((a, b) => {
    const v = pbSort.dir === 'asc' ? 1 : -1;
    return (a[pbSort.col] > b[pbSort.col] ? 1 : -1) * v;
  });
  const recent      = data?.recent || [];
  const total       = data?.total || 0;
  const totalPages  = Math.ceil(total / 50);
  const totalSubs   = summary.total_submissions || 0;

  // Compute unique page paths from page_breakdown for filter dropdown
  const pagePaths = (data?.page_breakdown || []).map(r => r.page_path).filter(Boolean);

  const toggleSort = (col) => {
    setPbSort(s => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const SortIcon = ({ col }) => pbSort.col === col
    ? (pbSort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
    : null;

  return (
    <div style={{ padding: '28px 32px', fontFamily: '"DM Sans", sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f2a35', margin: 0 }}>Portal Feedback</h1>
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
          Emoji reactions and comments submitted by learners and Partner RMs from the LevelUp Portal.
        </p>
      </div>

      {/* Row 1: Summary Card */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div
          data-testid="feedback-summary-card"
          style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '20px 28px', display: 'flex', alignItems: 'center', gap: 28 }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Average Rating</div>
            <div style={{ fontSize: 42, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
              {summary.avg_rating != null ? summary.avg_rating : '—'}
              <span style={{ fontSize: 18, color: '#9ca3af', fontWeight: 500 }}> / 5</span>
            </div>
            {summary.avg_rating != null && (
              <div style={{ marginTop: 8 }}>
                <StarFill rating={parseFloat(summary.avg_rating)} />
              </div>
            )}
          </div>
          <div style={{ width: 1, background: '#e5e7eb', alignSelf: 'stretch' }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Total Submissions</div>
            <div style={{ fontSize: 42, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{totalSubs.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Row 2: Emoji Breakdown */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '20px 24px', marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Reaction Breakdown</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          {[5, 4, 3, 2, 1].map(rating => {
            const row = breakdown.find(r => r.rating === rating);
            const count = row?.count || 0;
            const pct = totalSubs > 0 ? Math.round((count / totalSubs) * 100) : 0;
            const meta = EMOJI_MAP[rating];
            return (
              <div
                key={rating}
                data-testid={`emoji-bucket-${rating}`}
                style={{
                  flex: 1, background: '#fafafa', borderRadius: 10, padding: '14px 16px',
                  border: `1px solid ${meta.color}25`, textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 28, lineHeight: 1, marginBottom: 4 }}>{meta.emoji}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>{meta.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>{count.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>{pct}%</div>
                <div style={{ height: 4, background: '#e5e7eb', borderRadius: 2 }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 3: Filters */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            data-testid="filter-page-path"
            value={filterPage}
            onChange={e => setFilterPage(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minWidth: 200, color: '#374151' }}
          >
            <option value="">All Pages</option>
            {pagePaths.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          <select
            data-testid="filter-rating"
            value={filterRating}
            onChange={e => setFilterRating(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minWidth: 160, color: '#374151' }}
          >
            <option value="">All Ratings</option>
            {[5, 4, 3, 2, 1].map(r => (
              <option key={r} value={r}>{`${EMOJI_MAP[r].emoji} ${EMOJI_MAP[r].label} (${r})`}</option>
            ))}
          </select>

          <Button size="sm" onClick={applyFilters} data-testid="apply-filters-btn" style={{ background: '#194449', color: '#fff', border: 'none' }}>
            Apply
          </Button>
          {(filterPage || filterRating) && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Row 4: Page Breakdown Table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Page Breakdown</h2>
        </div>
        {pageBreakdown.length === 0 ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No data</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {[
                  { label: 'Page', col: 'page_path' },
                  { label: 'Avg Rating', col: 'avg_rating' },
                  { label: 'Submissions', col: 'count' },
                ].map(({ label, col }) => (
                  <th
                    key={col}
                    onClick={() => toggleSort(col)}
                    style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 12, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{label}<SortIcon col={col} /></span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageBreakdown.map((row, i) => (
                <tr
                  key={row.page_path}
                  data-testid={`page-breakdown-row-${i}`}
                  style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                >
                  <td style={{ padding: '9px 16px', color: '#111827' }}>{row.page_path}</td>
                  <td style={{ padding: '9px 16px', color: '#374151' }}>
                    {row.avg_rating != null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {row.avg_rating}
                        <span style={{ fontSize: 16 }}>{EMOJI_MAP[Math.round(row.avg_rating)]?.emoji || ''}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '9px 16px', color: '#374151' }}>{(row.count || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Row 5: Recent Feedback Table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Recent Feedback</h2>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>{total.toLocaleString()} total</span>
        </div>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : recent.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No feedback found</div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['User', 'Page', 'Reaction', 'Comment', 'Date'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map((row, i) => {
                  const meta = EMOJI_MAP[row.rating] || {};
                  const isExpanded = expandedRow === i;
                  const comment = row.comment || '';
                  const truncated = comment.length > 80;
                  return (
                    <tr
                      key={row.id ?? i}
                      data-testid={`feedback-row-${i}`}
                      style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa', verticalAlign: 'top' }}
                    >
                      <td style={{ padding: '9px 14px', color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.student_user_id
                          ? <span title={row.email || ''}>{row.student_user_id}</span>
                          : row.email || '—'}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#374151', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.page_path || '—'}</td>
                      <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 16 }}>{meta.emoji || '—'}</span>{' '}
                        <span style={{ fontSize: 11, color: meta.color || '#6b7280', fontWeight: 600 }}>{meta.label || ''}</span>
                      </td>
                      <td style={{ padding: '9px 14px', color: '#374151', maxWidth: 260 }}>
                        {comment ? (
                          <>
                            {isExpanded ? comment : comment.slice(0, 80) + (truncated ? '…' : '')}
                            {truncated && (
                              <button
                                onClick={() => setExpandedRow(isExpanded ? null : i)}
                                style={{ marginLeft: 4, fontSize: 11, color: '#194449', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                              >
                                {isExpanded ? 'less' : 'more'}
                              </button>
                            )}
                          </>
                        ) : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{fmtDate(row.submitted_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #f3f4f6', fontSize: 13, color: '#6b7280' }}>
                <span>{total} total</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage(p => p - 1); load(page - 1); }}>Prev</Button>
                  <span style={{ padding: '4px 10px', background: '#f3f4f6', borderRadius: 6 }}>Page {page} / {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); load(page + 1); }}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
